import { Input, Textarea, Label, Button } from '@creanizer/ui'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'

interface MiscEditorProps {
  authorNotes: string[]
  plotHooks: string[]
  customContext?: string
  onChange: (updates: { authorNotes?: string[], plotHooks?: string[], customContext?: string }) => void
}

export function MiscEditor({
  authorNotes,
  plotHooks,
  customContext,
  onChange
}: MiscEditorProps) {
  const [newNote, setNewNote] = useState('')
  const [newHook, setNewHook] = useState('')

  const handleAdd = (field: 'authorNotes' | 'plotHooks', value: string) => {
    if (value) {
      const current = field === 'authorNotes' ? authorNotes : plotHooks
      onChange({
        [field]: [...current, value]
      })
      if (field === 'authorNotes') setNewNote('')
      if (field === 'plotHooks') setNewHook('')
    }
  }

  const handleRemove = (field: 'authorNotes' | 'plotHooks', index: number) => {
    const current = field === 'authorNotes' ? authorNotes : plotHooks
    onChange({
      [field]: current.filter((_, i) => i !== index)
    })
  }

  const handleUpdate = (field: 'authorNotes' | 'plotHooks', index: number, value: string) => {
    const current = field === 'authorNotes' ? authorNotes : plotHooks
    const updated = [...current]
    updated[index] = value
    onChange({
      [field]: updated
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <Label className="font-medium text-lg">作者向けメモ</Label>
        <p className="text-sm text-muted-foreground mb-3">
          キャラクターを扱う際の注意点やポイント
        </p>
        <div className="space-y-2">
          {authorNotes.map((note, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={note}
                onChange={(e) => handleUpdate('authorNotes', index, e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRemove('authorNotes', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="例: 感情的になると方言が出る設定を忘れずに"
              onKeyPress={(e) => e.key === 'Enter' && handleAdd('authorNotes', newNote)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleAdd('authorNotes', newNote)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium text-lg">物語のフック</Label>
        <p className="text-sm text-muted-foreground mb-3">
          物語に組み込める要素やアイデア
        </p>
        <div className="space-y-2">
          {plotHooks.map((hook, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={hook}
                onChange={(e) => handleUpdate('plotHooks', index, e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRemove('plotHooks', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newHook}
              onChange={(e) => setNewHook(e.target.value)}
              placeholder="例: 秘密の出生が明らかになるタイミング"
              onKeyPress={(e) => e.key === 'Enter' && handleAdd('plotHooks', newHook)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleAdd('plotHooks', newHook)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium text-lg">カスタムコンテキスト</Label>
        <p className="text-sm text-muted-foreground mb-3">
          通ってきた展開・シナリオ、会話履歴、他キャラクターとの関係性の変化など自由に記述できます。
          他のジェネレータでこのキャラクターを読み込む際にコンテキストとして参照されます。
        </p>
        <Textarea
          value={customContext || ''}
          onChange={(e) => onChange({ customContext: e.target.value })}
          placeholder="例：&#10;・第3話でヒロインと和解し、以降は協力関係に&#10;・主人公との会話で過去のトラウマを打ち明けた&#10;・現在は王都を離れ、辺境の村で隠遁生活中"
          className="min-h-[200px] resize-y"
        />
      </div>
    </div>
  )
}